import { getActiveUserId, getDb } from "./core";
import type { MessageRow, OutboxEntry } from "./types";
import { TOMBSTONE_ENVELOPE_PLAINTEXT, isTombstonePlaintext } from "../crypto";
import { normalizeWorkspaceTopicCachedPlaintext } from "../workspaceTopicMessages";

function mergePendingFlag(existing: boolean | undefined, incoming: boolean | undefined): boolean | undefined {
  if (existing === false || incoming === false) return false;
  if (existing === true || incoming === true) return true;
  return undefined;
}

function mergeTrueOnlyFlag(existing: boolean | undefined, incoming: boolean | undefined): boolean | undefined {
  if (existing === true || incoming === true) return true;
  if (existing === false || incoming === false) return false;
  return undefined;
}

function mergeQueuedFlag(existing: boolean | undefined, incoming: boolean | undefined): boolean | undefined {
  if (existing === false || incoming === false) return false;
  if (existing === true || incoming === true) return true;
  return undefined;
}

function mergeFailedFlag(existing: boolean | undefined, incoming: boolean | undefined): boolean | undefined {
  if (incoming === false) return false;
  if (incoming === true) return true;
  return existing;
}

function mergeMessage(existing: MessageRow | undefined, incoming: MessageRow): MessageRow {
  if (!existing) {
    const base =
      incoming.deleted || isTombstonePlaintext(incoming.plaintext)
        ? { ...incoming, plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT, deleted: true }
        : incoming;
    const normalized = normalizeWorkspaceTopicCachedPlaintext(base.peerId, base.plaintext);
    return normalized === base.plaintext ? base : { ...base, plaintext: normalized };
  }
  const selfId = getActiveUserId();
  let createdAt = incoming.created_at || existing.created_at;
  if (
    selfId &&
    existing.from_id === selfId &&
    existing.pending === true &&
    incoming.from_id === selfId
  ) {
    createdAt = existing.created_at;
  }

  const merged = {
    ...existing,
    ...incoming,
    created_at: createdAt,
    plaintext:
      incoming.deleted || isTombstonePlaintext(incoming.plaintext)
        ? TOMBSTONE_ENVELOPE_PLAINTEXT
        : incoming.plaintext || existing.plaintext,
    deleted: incoming.deleted === true || isTombstonePlaintext(incoming.plaintext) ? true : existing.deleted,
    pending: mergePendingFlag(existing.pending, incoming.pending),
    queued: mergeQueuedFlag(existing.queued, incoming.queued),
    failed: mergeFailedFlag(existing.failed, incoming.failed),
    delivered: mergeTrueOnlyFlag(existing.delivered, incoming.delivered),
    read: mergeTrueOnlyFlag(existing.read, incoming.read),
    seenLocal: mergeTrueOnlyFlag(existing.seenLocal, incoming.seenLocal),
  };
  const normalized = normalizeWorkspaceTopicCachedPlaintext(merged.peerId, merged.plaintext);
  return normalized === merged.plaintext ? merged : { ...merged, plaintext: normalized };
}

function normalizeMessageRow(row: MessageRow): MessageRow {
  const normalized = normalizeWorkspaceTopicCachedPlaintext(row.peerId, row.plaintext);
  return normalized === row.plaintext ? row : { ...row, plaintext: normalized };
}

async function persistNormalizedRows(db: Awaited<ReturnType<typeof getDb>>, rows: MessageRow[]): Promise<MessageRow[]> {
  const normalizedRows = rows.map(normalizeMessageRow);
  const changedRows = normalizedRows.filter((row, i) => row !== rows[i]);
  if (changedRows.length === 0) return rows;
  const tx = db.transaction("messages", "readwrite");
  for (const row of changedRows) {
    await tx.store.put(row);
  }
  await tx.done;
  return normalizedRows;
}

export async function saveMessage(m: MessageRow): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("messages", "readwrite");
  const existing = await tx.store.get(m.id);
  await tx.store.put(mergeMessage(existing, m));
  await tx.done;
}

export async function listMessagesForPeer(peerId: string): Promise<MessageRow[]> {
  const db = await getDb();
  const idx = db.transaction("messages").store.index("by-peer");
  const rows = await idx.getAll(peerId);
  return persistNormalizedRows(db, rows);
}

export const CHAT_PAGE_SIZE = 50;

export async function listRecentMessagesForPeer(peerId: string, limit: number): Promise<MessageRow[]> {
  const db = await getDb();
  const tx = db.transaction("messages", "readonly");
  const idx = tx.store.index("by-peer-sort");
  const lower: [string, number, string] = [peerId, 0, ""];
  const upper: [string, number, string] = [peerId, Number.MAX_SAFE_INTEGER, "\uffff"];
  const range = IDBKeyRange.bound(lower, upper);
  let cursor = await idx.openCursor(range, "prev");
  const out: MessageRow[] = [];
  while (cursor && out.length < limit) {
    out.push(cursor.value);
    cursor = await cursor.continue();
  }
  await tx.done;
  out.reverse();
  return persistNormalizedRows(db, out);
}

export async function listOlderMessagesForPeer(
  peerId: string,
  beforeCreatedAt: number,
  beforeId: string,
  limit: number,
): Promise<MessageRow[]> {
  const db = await getDb();
  const tx = db.transaction("messages", "readonly");
  const idx = tx.store.index("by-peer-sort");
  const edge: [string, number, string] = [peerId, beforeCreatedAt, beforeId];
  const range = IDBKeyRange.upperBound(edge, true);
  let cursor = await idx.openCursor(range, "prev");
  const out: MessageRow[] = [];
  while (cursor && out.length < limit) {
    out.push(cursor.value);
    cursor = await cursor.continue();
  }
  await tx.done;
  out.reverse();
  return persistNormalizedRows(db, out);
}

export async function getMessage(id: string): Promise<MessageRow | undefined> {
  const db = await getDb();
  const row = await db.get("messages", id);
  if (!row) return undefined;
  const normalized = normalizeMessageRow(row);
  if (normalized !== row) {
    await db.put("messages", normalized);
  }
  return normalized;
}

/** Latest DM message time for a peer (by `by-peer-sort` index), or null if no messages. */
export async function getLatestMessageTimestampForPeer(peerId: string): Promise<number | null> {
  const db = await getDb();
  const tx = db.transaction("messages", "readonly");
  const idx = tx.store.index("by-peer-sort");
  const lower: [string, number, string] = [peerId, 0, ""];
  const upper: [string, number, string] = [peerId, Number.MAX_SAFE_INTEGER, "\uffff"];
  const range = IDBKeyRange.bound(lower, upper);
  let cursor = await idx.openCursor(range, "prev");
  const ts = cursor?.value.created_at ?? null;
  await tx.done;
  return ts;
}

export async function mergeMessagePatch(id: string, patch: Partial<MessageRow>): Promise<MessageRow | null> {
  const db = await getDb();
  const tx = db.transaction("messages", "readwrite");
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    return null;
  }

  const nextPatch = { ...patch };
  if (nextPatch.pending === true && existing.pending === false) delete nextPatch.pending;
  if (nextPatch.queued === true && existing.queued === false) delete nextPatch.queued;
  if (nextPatch.failed === true && existing.failed === false) delete nextPatch.failed;
  if (nextPatch.delivered === false && existing.delivered === true) delete nextPatch.delivered;
  if (nextPatch.read === false && existing.read === true) delete nextPatch.read;
  if (nextPatch.seenLocal === false && existing.seenLocal === true) delete nextPatch.seenLocal;

  const merged = normalizeMessageRow(mergeMessage(existing, { ...existing, ...nextPatch }));
  await tx.store.put(merged);
  await tx.done;
  return merged;
}

export async function listUnreadCountsByPeer(): Promise<Record<string, number>> {
  const db = await getDb();
  const rows = await db.getAll("messages");
  const counts: Record<string, number> = {};
  for (const row of rows) {
    if (row.from_id !== row.peerId || row.seenLocal === true || row.read === true) continue;
    counts[row.peerId] = (counts[row.peerId] ?? 0) + 1;
  }
  return counts;
}

export async function markPeerMessagesSeen(peerId: string): Promise<string[]> {
  const db = await getDb();
  const tx = db.transaction("messages", "readwrite");
  const idx = tx.store.index("by-peer");
  const rows = await idx.getAll(peerId);
  const updatedIds: string[] = [];
  for (const row of rows) {
    if (row.from_id !== peerId || row.seenLocal === true || row.read === true) continue;
    row.seenLocal = true;
    updatedIds.push(row.id);
    await tx.store.put(row);
  }
  await tx.done;
  return updatedIds;
}

export async function deleteMessageById(id: string): Promise<void> {
  const db = await getDb();
  await db.delete("messages", id);
}

/** Max queued sends stored locally (plan cap). */
export const MAX_OUTBOX_ENTRIES = 100;

/** Flush attempts per outbox entry before marking `failed`. */
export const MAX_OUTBOX_FLUSH_ATTEMPTS = 5;

export async function countOutboxEntries(): Promise<number> {
  const db = await getDb();
  return db.count("outbox");
}

export async function listOutboxEntriesSorted(): Promise<OutboxEntry[]> {
  const db = await getDb();
  const rows = await db.getAll("outbox");
  rows.sort((a, b) =>
    a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
  );
  return rows;
}

export async function listOutboxEntriesForPeer(peerId: string): Promise<OutboxEntry[]> {
  const db = await getDb();
  const tx = db.transaction("outbox", "readonly");
  const idx = tx.store.index("by-peer-sort");
  const lower: [string, number, string] = [peerId, 0, ""];
  const upper: [string, number, string] = [peerId, Number.MAX_SAFE_INTEGER, "\uffff"];
  const range = IDBKeyRange.bound(lower, upper);
  const out: OutboxEntry[] = [];
  let cursor = await idx.openCursor(range);
  while (cursor) {
    out.push(cursor.value);
    cursor = await cursor.continue();
  }
  await tx.done;
  out.sort((a, b) =>
    a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
  );
  return out;
}

export async function getOutboxEntry(id: string): Promise<OutboxEntry | undefined> {
  const db = await getDb();
  return db.get("outbox", id);
}

export async function saveOutboxEntry(entry: OutboxEntry): Promise<void> {
  const db = await getDb();
  const n = await db.count("outbox");
  const existing = await db.get("outbox", entry.id);
  if (!existing && n >= MAX_OUTBOX_ENTRIES) {
    throw new Error("outbox full");
  }
  await db.put("outbox", entry);
}

export async function removeOutboxEntry(id: string): Promise<void> {
  const db = await getDb();
  await db.delete("outbox", id);
}
